import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/hooks';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  className?: string;
}

/**
 * A compact two-state switch. The knob slides between a sun (Twitter light)
 * and a moon (matte black). Deliberately not a shadcn ghost icon button —
 * it reads as a physical toggle.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      onClick={toggleTheme}
      className={cn(
        'group relative inline-flex h-11 w-[4.25rem] items-center rounded-full border border-border bg-muted/60 px-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className
      )}
    >
      <span
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
          isDark ? 'translate-x-[1.75rem]' : 'translate-x-0'
        )}
      >
        {isDark ? (
          <Moon className="h-3.5 w-3.5 text-primary" strokeWidth={2.2} />
        ) : (
          <Sun className="h-3.5 w-3.5 text-primary" strokeWidth={2.2} />
        )}
      </span>
    </button>
  );
}
